/**
 * PromptEditor — the Ion Next system prompts, stored in the database
 * (plan docs/plans/2026-10-27-ion-next-db-prompt.md), opened from the status bar.
 *
 * One key at a time (the backend's key list: the profiles' prompts and the
 * answer-style texts `style:*`, which are not pinned: an activated style text
 * applies to the next turn of every conversation). The textarea starts with the active text; "Save as
 * new version" stores the edit as the key's next version and activates it, so
 * a NEW conversation uses it at once while running ones keep the version they
 * are pinned to. The version list loads any older text and activates it (the
 * rollback). Versions are append-only: nothing is overwritten or deleted.
 *
 * For the `ion` key a warning names the load-bearing markers (`[[V:` chip
 * rules, `analysis_conventions`) that the loaded text has and the draft lost.
 */
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import {
  activateIonNextPrompt,
  createIonNextPrompt,
  getIonNextPrompt,
  listIonNextPrompts,
} from '../../../services/adminService.js';

/** Text the `ion` prompt must keep: the chip rules and the house-conventions pointer. */
export const ION_PROMPT_MARKERS = ['[[V:', 'analysis_conventions'];

// Same bound as the backend (native/prompts.py PROMPT_MAX_CHARS).
const MAX_CHARS = 60000;

const KEY_LABELS = {
  ion: 'Ion prompt (profile ion)',
  baseline: 'Baseline prompt (profile baseline)',
  'style:explicit': 'Answer style: Explicit',
  'style:compact': 'Answer style: Compact',
  'style:tldr': 'Answer style: TL;DR',
};

export function promptKeyLabel(key) {
  return KEY_LABELS[key] || key;
}

/** Markers of `ION_PROMPT_MARKERS` present in `original` but missing in `draft` (ion key only). */
export function missingPromptMarkers(key, original, draft) {
  if (key !== 'ion') return [];
  return ION_PROMPT_MARKERS.filter((m) => (original || '').includes(m) && !(draft || '').includes(m));
}

/** Who picks up a newly active version: prompts are pinned per conversation, style texts are not. */
function appliesTo(key) {
  return String(key).startsWith('style:')
    ? 'the next turn of every conversation with this style uses it'
    : 'new conversations use it now';
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toISOString().slice(0, 16).replace('T', ' ');
}

export function PromptEditor({ onClose, onChanged, initialKey = 'ion' }) {
  const [keys, setKeys] = useState([]);
  const [key, setKey] = useState(initialKey);
  // The version whose text the draft started from: {id, version, body, isActive, sha256}.
  const [loaded, setLoaded] = useState(null);
  const [draft, setDraft] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  // The latest load wins (a quick key switch must not show the older key's text).
  const loadSeq = useRef(0);

  const refresh = useCallback(async () => {
    const data = await listIonNextPrompts();
    const list = data?.keys || [];
    setKeys(list);
    return list;
  }, []);

  const loadVersion = useCallback(async (id) => {
    const seq = ++loadSeq.current;
    const p = await getIonNextPrompt(id);
    if (seq !== loadSeq.current) return;
    setLoaded(p);
    setDraft(p.body || '');
  }, []);

  // A key switch: its versions, then its active text in the textarea.
  useEffect(() => {
    let cancelled = false;
    setError(null);
    setNotice(null);
    (async () => {
      try {
        const list = await refresh();
        if (cancelled) return;
        const entry = list.find((k) => k.key === key);
        if (entry?.activeId != null) {
          await loadVersion(entry.activeId);
        } else {
          loadSeq.current += 1;
          setLoaded(null);
          setDraft('');
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Could not load the prompts');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [key, refresh, loadVersion]);

  const entry = keys.find((k) => k.key === key);
  const versions = entry?.versions || [];
  const missing = missingPromptMarkers(key, loaded?.body, draft);
  const tooLong = draft.length > MAX_CHARS;
  const unchanged = loaded != null && draft === loaded.body;
  const canSave = !busy && draft.trim().length > 0 && !tooLong && !unchanged;

  const save = async () => {
    if (!canSave) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const saved = await createIonNextPrompt({ key, body: draft, note: note.trim() || null, activate: true });
      loadSeq.current += 1;
      setLoaded(saved);
      setDraft(saved.body ?? draft);
      setNote('');
      setNotice(`Saved as version ${saved.version} and activated: ${appliesTo(key)}.`);
      await refresh();
      onChanged?.();
    } catch (e) {
      setError(e?.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const activate = async (v) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await activateIonNextPrompt(v.id);
      await refresh();
      await loadVersion(v.id);
      setNotice(`Version ${v.version} is active: ${appliesTo(key)}.`);
      onChanged?.();
    } catch (e) {
      setError(e?.message || 'Activation failed');
    } finally {
      setBusy(false);
    }
  };

  const load = async (v) => {
    setError(null);
    setNotice(null);
    try {
      await loadVersion(v.id);
    } catch (e) {
      setError(e?.message || 'Could not load the version');
    }
  };

  return (
    <section className="ion-next-prompt-editor admin-aim-admin-note" aria-label="Ion Next prompt editor">
      <div className="ion-next-prompt-editor__head" style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <strong>Prompts</strong>
        <label className="admin-ai-model-select">
          <span className="admin-ai-model-select-label">Prompt</span>
          <select
            name="ion-next-prompt-key"
            className="admin-ai-model-select-input"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            disabled={busy}
          >
            {(keys.length ? keys.map((k) => k.key) : [key]).map((k) => (
              <option key={k} value={k}>{promptKeyLabel(k)}</option>
            ))}
          </select>
        </label>
        {loaded && (
          <span className="ion-next-prompt-editor__from" title={`sha256 ${loaded.sha256 || ''}`}>
            Editing from version {loaded.version}
            {loaded.isActive ? ' (active)' : ''}
          </span>
        )}
        <button type="button" className="admin-ai-btn admin-ai-btn--ghost" onClick={onClose}>Close</button>
      </div>
      <p className="ion-next-prompt-editor__hint">
        {String(key).startsWith('style:')
          ? 'Saving stores a new version and activates it. Answer-style texts are not pinned: the next turn of every conversation with this style uses it.'
          : 'Saving stores a new version and activates it. New conversations use it immediately; running conversations keep the version they started with.'}
      </p>
      {error && <div className="admin-aim-error-banner" role="alert">{error}</div>}
      {notice && <div className="ion-next-prompt-editor__notice" role="status">{notice}</div>}
      <textarea
        name="ion-next-prompt-body"
        className="admin-ai-input"
        rows={18}
        style={{ width: '100%', fontFamily: 'monospace' }}
        value={draft}
        onInput={(e) => setDraft(e.target.value)}
        disabled={busy}
      />
      <div className="ion-next-prompt-editor__count" style={{ fontSize: '0.85em' }}>
        {draft.length.toLocaleString('en-US')} / {MAX_CHARS.toLocaleString('en-US')} characters
        {tooLong ? ' — too long' : ''}
      </div>
      {missing.length > 0 && (
        <div className="admin-aim-error-banner ion-next-prompt-editor__markers" role="alert">
          Warning: the draft no longer contains {missing.map((m) => `"${m}"`).join(' and ')}. The chat
          relies on it (value chips, house conventions).
        </div>
      )}
      <div className="ion-next-prompt-editor__save" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', margin: '0.5rem 0' }}>
        <input
          name="ion-next-prompt-note"
          className="admin-ai-model-select-input"
          placeholder="Note: what changed and why"
          maxLength={500}
          value={note}
          onInput={(e) => setNote(e.target.value)}
          disabled={busy}
          style={{ flex: 1 }}
        />
        <button type="button" className="admin-ai-btn admin-ai-btn--send" onClick={save} disabled={!canSave}>
          Save as new version
        </button>
      </div>
      <table className="ion-next-prompt-editor__versions" style={{ width: '100%', fontSize: '0.85em' }}>
        <thead>
          <tr>
            <th align="left">Version</th>
            <th align="left">Created</th>
            <th align="left">Author</th>
            <th align="left">Note</th>
            <th align="left">sha256</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {versions.length === 0 ? (
            <tr><td colSpan={6}>No stored version yet: the code prompt is in use.</td></tr>
          ) : (
            versions.map((v) => (
              <tr key={v.id} className={v.isActive ? 'is-active' : ''} data-version={v.version}>
                <td>
                  v{v.version}
                  {v.isActive ? ' · active' : ''}
                </td>
                <td>{formatDate(v.createdAt)}</td>
                <td>{v.author || '—'}</td>
                <td>{v.note || ''}</td>
                <td><code>{String(v.sha256 || '').slice(0, 12)}</code></td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button type="button" className="admin-ai-link" onClick={() => load(v)} disabled={busy}>Load</button>{' '}
                  {!v.isActive && (
                    <button type="button" className="admin-ai-link" onClick={() => activate(v)} disabled={busy}>
                      Activate
                    </button>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </section>
  );
}
